/**
 * Heuristic AI controller. Plays to build a strong hand before knocking — which
 * is what produces realistic game scores (~290 avg / ~1450 table total), close
 * to the historical data. Deterministic given a seed. Later: tune against the
 * 965-game history.
 */
import { evaluate, compareHands, HandCategory, type HandRank } from "./handEval.ts";
import { borders } from "./states.ts";
import { makeRng, type Rng } from "./rng.ts";
import type { Action, Controller, TurnView } from "./whiskey.ts";
import type { Card } from "./cards.ts";
import { PLAIN_STYLE, type SimpleStyle } from "./personas.ts";

export class AIController implements Controller {
  private readonly rng: Rng;
  /** How this AI plays (a persona's style, or the plain simple AI). */
  private readonly style: SimpleStyle;
  constructor(seed = 1, style: SimpleStyle = PLAIN_STYLE) {
    this.rng = makeRng(seed);
    this.style = style;
  }

  decideAction(view: TurnView): Action {
    const current = evaluate(view.hand);

    // Look at the middle before deciding — unless the hand is junk and worth a
    // blind "Double Dumper" gamble on the very first turn.
    if (!view.middleRevealed) {
      if (
        current.category === HandCategory.Zipper &&
        view.legal.includes("TAKE_MIDDLE") &&   // never the dealer
        view.turnsTaken === 0 &&
        view.stockCount > 6 &&
        this.rng.next() < this.style.blindGamble
      ) {
        return { type: "TAKE_MIDDLE" };
      }
      return { type: "REVEAL" };
    }

    const middleCards = view.middle.filter((c): c is Card => c !== null);
    const swap = this.bestSwap(view.hand, middleCards, current);

    // A knocker has ended it — take the best last move: the whole middle if it beats anything a
    // single swap gives (and our own hand), else the best improving swap, else stand.
    if (view.someoneKnocked) {
      const best = swap ? this.after(view.hand, swap, middleCards) : current;
      if (middleCards.length === 5 && view.legal.includes("TAKE_MIDDLE") && compareHands(evaluate(middleCards), best) > 0) return { type: "TAKE_MIDDLE" };
      return swap ?? { type: "KEEP" };
    }

    // Satisfied (trips+, or a straight+ for a patient player): knock now to deny opponents more draws.
    if (current.category >= this.style.knockAt) return { type: "KNOCK" };

    // Grab the whole middle if it's a strong made hand well above ours.
    if (middleCards.length === 5 && view.legal.includes("TAKE_MIDDLE")) {
      const mid = evaluate(middleCards);
      if (mid.category >= this.style.takeMiddleAt && compareHands(mid, current) > 0) {
        const best = swap ? this.after(view.hand, swap, middleCards) : current;
        if (compareHands(mid, best) > 0) return { type: "TAKE_MIDDLE" };
      }
    }

    // Improve while a better card sits in the middle (a looser player sometimes digs instead).
    const slip = this.style.slip > 0 && view.stockCount > 0 && this.rng.next() < this.style.slip;
    if (swap && !slip) return swap;

    // Two pair and nothing to grab: knock (after a turn, or straight away for an eager knocker).
    if (current.category >= HandCategory.TwoPair && view.turnsTaken >= this.style.pairKnockTurn) return { type: "KNOCK" };

    // Otherwise dig through the bones, ditching our weakest card.
    if (view.stockCount > 0 && view.turnsTaken < this.style.blindTurns) {
      return { type: "BLIND", give: this.weakestCardIndex(view.hand) };
    }

    // Ran out of road: knock with anything worth points, else pass.
    return current.category >= HandCategory.Pair ? { type: "KNOCK" } : { type: "KEEP" };
  }

  /**
   * The AI knocks via decideAction (above); this post-exchange hook is a no-op
   * so the "exchange then knock" option is exposed to the human player only.
   */
  decideKnock(): boolean {
    return false;
  }

  /** Best single swap (hand card ↔ a specific middle card) that improves us. */
  private bestSwap(hand: Card[], middle: Card[], current: HandRank): Action | null {
    let best: { give: number; take: number; rank: HandRank } | null = null;
    for (let ti = 0; ti < middle.length; ti++) {
      for (let gi = 0; gi < hand.length; gi++) {
        const trial = hand.slice();
        trial[gi] = middle[ti];
        const r = evaluate(trial);
        if (compareHands(r, current) > 0 && (!best || compareHands(r, best.rank) > 0)) {
          best = { give: gi, take: ti, rank: r };
        }
      }
    }
    return best ? { type: "SWAP_ONE", give: best.give, take: best.take } : null;
  }

  private after(hand: Card[], swap: Action, middle: Card[]): HandRank {
    if (swap.type !== "SWAP_ONE") return evaluate(hand);
    const trial = hand.slice();
    trial[swap.give] = middle[swap.take];
    return evaluate(trial);
  }

  private weakestCardIndex(hand: Card[]): number {
    const counts = new Map<number, number>();
    for (const c of hand) counts.set(c.rank, (counts.get(c.rank) ?? 0) + 1);
    let idx = 0;
    let worst = Infinity;
    for (let i = 0; i < hand.length; i++) {
      const paired = (counts.get(hand[i].rank) ?? 0) > 1;
      const score = hand[i].rank + (paired ? 100 : 0); // keep pairs
      if (score < worst) {
        worst = score;
        idx = i;
      }
    }
    return idx;
  }

  chooseState(unclaimed: string[], owned: string[]): string {
    const contig = unclaimed.find((s) => owned.some((o) => borders(s, o)));
    return contig ?? this.rng.pick(unclaimed);
  }

  chooseCapital(owned: string[]): string {
    return owned.length ? this.rng.pick(owned) : "";
  }
}
